import { Show, mergeProps, splitProps } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { cn, tv } from 'tailwind-variants';
import { ActivityIndicator } from '../components/activity-indicator.tsx';
import { useFormContext } from './form.tsx';
import type { Link } from '../components/link.tsx';
import type { IconComponent } from '@bbr/icons';
import type { JSX, ComponentProps } from 'solid-js';
import type { VariantProps } from 'tailwind-variants';

type Icon = {
	icon?: IconComponent;
	iconPosition?: 'start' | 'end';
	iconLabel?: never;
};

type IconOnly = {
	icon: IconComponent;
	iconPosition: 'only';
	iconLabel: string;
	children?: never;
};

type AllProps = VariantProps<typeof buttonStyles> &
	(Icon | IconOnly) & {
		// Extra classes for extreme cases
		class?: never;
		disabled?: boolean;
		showActivityIndicator?: boolean;
	};
type LinkProps<T extends typeof Link | 'a' = typeof Link> = {
	as: T;
} & AllProps &
	Omit<ComponentProps<T>, 'class'>;

export type ButtonProps = {
	as?: 'button' | undefined;
} & AllProps &
	Omit<JSX.ButtonHTMLAttributes<HTMLButtonElement>, 'class'>;

type SpanProps = {
	as: 'span';
} & AllProps &
	Omit<JSX.HTMLAttributes<HTMLSpanElement>, 'class'>;

type AProps = {
	as: 'a';
} & AllProps &
	Omit<JSX.AnchorHTMLAttributes<HTMLAnchorElement>, 'class'>;

type Props = LinkProps | ButtonProps | SpanProps | AProps;

export function Button(inputProps: Props) {
	const { data } = useFormContext();

	const allProps = mergeProps({ as: 'button', showActivityIndicator: false }, inputProps);
	const [props, rest] = splitProps(allProps, [
		'action',
		'appearance',
		'as',
		'children',
		'icon',
		'iconLabel',
		'iconPosition',
		'parentAsHitTarget',
		'rounding',
		'showActivityIndicator',
		'size',
		'width',
	]);

	return (
		<Dynamic
			tabIndex={0}
			type={!props.as || props.as === 'button' ? 'button' : undefined}
			{...rest}
			component={props.as}
			disabled={
				// @ts-expect-error discriminate union failing
				props.as === 'button' && rest.type === 'submit' ? data().pending || rest.disabled : rest.disabled
			}
			aria-disabled={
				// @ts-expect-error discriminate union failing
				props.as === 'button' && rest.type === 'submit' ? data().pending || rest.disabled : rest.disabled
			}
			formnovalidate
			class={cn(
				buttonStyles({
					action: props.action,
					rounding: props.rounding,
					appearance: props.appearance,
					parentAsHitTarget: props.parentAsHitTarget,
					size: props.size,
					width: props.width,
				}),
				!props.icon && '**:data-icon:text-rose-700',
			)}
		>
			<Show when={props.iconPosition === 'only' || !props.iconPosition || props.iconPosition === 'start'}>
				<Dynamic component={props.icon} class="shrink-0" />
			</Show>
			<Show when={props.iconPosition !== 'only'}>
				<Show
					when={
						// @ts-expect-error discriminate union failing
						props.showActivityIndicator && data().pending && rest.type === 'submit'
					}
				>
					<ActivityIndicator aria-label="Loading" size="sm" />
				</Show>
				{props.children}
			</Show>
			<Show when={props.iconPosition === 'end'}>
				<Dynamic component={props.icon} class="shrink-0" />
			</Show>
		</Dynamic>
	);
}

export const buttonStyles = tv({
	base: 'group/button pointer-events-auto flex cursor-pointer flex-row items-center justify-center gap-x-2 text-center font-semibold text-wrap whitespace-nowrap transition-all duration-150 outline-none hover:-translate-y-px focus-visible:ring-4 active:translate-y-0.5 aria-disabled:cursor-not-allowed aria-disabled:hover:translate-y-0 aria-disabled:active:translate-y-0 motion-safe:aria-disabled:active:animate-shake',
	variants: {
		action: {
			primary: 'focus-visible:ring-yellow-500/50',
			danger: 'focus-visible:ring-rose-500/50',
		},
		appearance: {
			default: 'border border-zinc-300 aria-disabled:bg-zinc-100 dark:border-zinc-800 dark:aria-disabled:bg-zinc-900',
			primary: 'aria-disabled:bg-zinc-200 dark:aria-disabled:bg-zinc-800',
			outline:
				'border aria-disabled:bg-zinc-100/50 aria-disabled:text-zinc-500 dark:aria-disabled:bg-zinc-900/50 dark:aria-disabled:text-zinc-500',
			subtle: 'aria-disabled:bg-zinc-100/50 dark:aria-disabled:bg-zinc-900/50',
			textlink:
				'inline-flex font-normal text-yellow-700 underline decoration-transparent decoration-0 -underline-offset-4 transition-all hover:decoration-current hover:decoration-wavy hover:decoration-1 hover:underline-offset-2 dark:text-yellow-400',
		},
		parentAsHitTarget: {
			true: 'after:absolute after:inset-0',
			false: '',
		},
		rounding: {
			circle: 'aspect-square rounded-full',
			full: 'rounded-full',
			md: 'rounded-md',
			sm: 'rounded-sm px-2',
		},
		size: {
			xs: 'text-sm',
			sm: 'text-base',
			md: 'text-lg',
			lg: 'text-2xl',
		},
		width: {
			full: 'w-full grow',
			fit: 'w-fit shrink grow-0',
		},
	},
	compoundVariants: [
		{
			rounding: ['full', 'md'],
			size: 'sm',
			class: 'px-2 py-0.75',
		},
		{
			rounding: ['full', 'md'],
			size: 'md',
			class: 'px-3 py-1.5',
		},
		{
			rounding: ['full', 'md'],
			size: 'lg',
			class: 'px-4 py-2',
		},
		{
			rounding: 'circle',
			size: 'lg',
			class: 'p-2',
		},
		{
			rounding: 'circle',
			size: 'md',
			class: 'p-1.5',
		},
		{
			rounding: 'circle',
			size: 'sm',
			class: 'p-1',
		},
		{
			rounding: ['full', 'md'],
			appearance: ['default', 'primary', 'outline', 'subtle'],
			size: 'xs',
			class: 'px-1.5',
		},

		{
			appearance: 'default',
			action: 'primary',
			class:
				'bg-white active:bg-yellow-200 aria-disabled:text-yellow-700 dark:bg-zinc-900 dark:hover:border-yellow-900 dark:aria-disabled:text-current',
		},
		{
			appearance: 'primary',
			action: 'primary',
			class:
				'bg-yellow-400 text-zinc-950 hover:bg-yellow-500 aria-disabled:text-yellow-700/80 dark:bg-yellow-400 dark:hover:bg-yellow-600 dark:aria-disabled:text-yellow-500/70',
		},
		{
			appearance: 'outline',
			action: 'primary',
			class:
				'border-yellow-400 aria-disabled:border-yellow-200 dark:border-yellow-600 dark:aria-disabled:border-yellow-950',
		},
		{
			appearance: 'subtle',
			action: 'primary',
			class:
				'hover:bg-current/20 aria-current-page:bg-zinc-100 dark:aria-current-page:bg-zinc-800 aria-pressed:bg-yellow-500/20',
		},

		{
			appearance: 'default',
			action: 'danger',
			class:
				'text-rose-700 active:bg-rose-200 aria-disabled:text-rose-700/80 dark:text-rose-400 dark:hover:border-rose-900 dark:hover:text-rose-50 dark:aria-disabled:text-rose-500/70',
		},
		{
			appearance: 'primary',
			action: 'danger',
			class:
				'bg-rose-500 text-zinc-950 hover:bg-rose-600 aria-disabled:text-rose-700/80 dark:aria-disabled:text-rose-500/70',
		},
		{
			appearance: 'outline',
			action: 'danger',
			class:
				'border-rose-600 aria-disabled:border-rose-300 dark:border-rose-500 dark:hover:bg-yellow-500/50 dark:aria-disabled:border-rose-950',
		},
		{
			appearance: 'subtle',
			action: 'danger',
			class:
				'text-rose-600 aria-disabled:text-rose-700/80 aria-disabled:hover:text-rose-700/80 dark:text-rose-400 dark:hover:text-zinc-950 aria-pressed:bg-rose-500/20',
		},

		{
			appearance: ['default', 'outline'],
			action: 'primary',
			class: 'hover:bg-yellow-400 hover:text-zinc-950 aria-pressed:bg-yellow-500/20',
		},
		{
			appearance: ['default', 'outline', 'subtle'],
			action: 'danger',
			class: 'hover:bg-rose-500/40 dark:hover:bg-rose-500/60 aria-pressed:bg-rose-500/20',
		},
	],
	defaultVariants: {
		action: 'primary',
		appearance: 'default',
		parentAsHitTarget: false,
		rounding: 'full',
		size: 'md',
		width: 'fit',
	},
});
